'use client'
import {
  ButtonLink,
  InformationDialog,
  InformationDialogSimple,
  LevelsBadge,
  LevelsSlider,
  PaymentType,
  ValueWithUnit,
} from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import {
  Card,
  Col,
  LinearBlur,
  MultiplyBadge,
  Row,
  Separator,
  Typography,
} from '@repo/ui/components'
import { useState, useEffect, type FC } from 'react'

import {
  UserReferralStats,
  useGetLoyaltyPortfolio,
  useUserAuthDetails,
} from '@repo/services'
import { useIsDesktop, useLocalStorage } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronUpIcon, XIcon } from 'lucide-react'
import Image from 'next/image'
import { Icon } from '@repo/ui/components'
import { InviteDetails } from './invite'

export const BalanceCard: FC = () => {
  const { t, tRich } = useTranslate()
  const { data: { data: { portfolio = {} } = {} } = {} } =
    useGetLoyaltyPortfolio()
  const { data: { data: { details = {} } = {} } = {} } = useUserAuthDetails()

  const isDesktop = useIsDesktop()
  const [closeCount, setCloseCount] = useLocalStorage<number>(
    'club-balance-card-close-count',
    0,
  )

  const shouldShowFullByDefault = isDesktop || (closeCount < 3 && !isDesktop)

  const [showFull, setShowFull] = useState(shouldShowFullByDefault)

  // Update showFull when closeCount changes (e.g., after user closes 3 times)
  useEffect(() => {
    if (!isDesktop && closeCount >= 3) {
      setShowFull(false)
    }
  }, [closeCount, isDesktop])

  const coin = portfolio.balance ?? 0
  const value = (portfolio.balance ?? 0) * 1000
  const coefficient = Math.floor((portfolio.coefficient ?? 0) * 100) / 100
  const level: 1 | 2 | 3 | 4 | 5 = ((portfolio.level?.current ?? 0) + 1) as
    | 1
    | 2
    | 3
    | 4
    | 5
  const needToNextLevel = portfolio.level?.pointsToNext ?? 1479

  const handleClose = () => {
    setShowFull(false)
    if (!isDesktop) {
      setCloseCount((prev) => prev + 1)
    }
  }
  const handleOpen = () => {
    setShowFull(true)
  }
  return (
    <Col
      className={cn(
        'gap-md p-sm md:p-0 fixed shadow-2xl md:shadow-none md:relative left-0 right-0',
        !showFull ? 'bottom-0' : '-mb-sm md:mb-0 -bottom-2 md:bottom-0',
      )}
    >
      <Card
        className={cn(
          !showFull
            ? 'shadow-2xl py-3xs rounded-t-2xl rounded-b-2xl'
            : 'py-xl md:py-0 rounded-t-2xl',
          'w-full items-center',
          'px-sm md:py-0 md:px-md rounded-b-0 md:rounded-t-md md:rounded-b-md z-1',
        )}
        style={{ overflow: 'hidden' }}
        withoutBorder
      >
        <AnimatePresence initial={false} mode='wait'>
          {showFull ? (
            <motion.div
              key='full'
              initial={{ opacity: 0, height: 56 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 56 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className='flex flex-col gap-md w-full justify-center items-center m-auto'
            >
              {!isDesktop && (
                <span
                  onClick={handleClose}
                  className="bg-neutral-surface-dark p-xs hover:bg-neutral-surface-dark/50 rounded-full cursor-pointer focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-9 left-9 transition-opacity focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                >
                  <XIcon />
                </span>
              )}

              <InformationDialogSimple
                title={t('clubPage_balance_coins')}
                trigger={
                  <Col className='gap-md items-center'>
                    <Image
                      src='/images/club-gem.svg'
                      alt='coin'
                      width={70}
                      height={70}
                    />
                    <Col className='items-center gap-2xs'>
                      <Row className='justify-center gap-xs w-fit'>
                        <Typography color='text-neutral-surface-100' size='xs'>
                          {t('clubPage_balance_coins')}
                        </Typography>
                        <Icon
                          name='InfoCircle'
                          size='xs'
                          color={'fill-neutral-surface-20'}
                          className='cursor-pointer'
                        />
                      </Row>

                      <ValueWithUnit
                        value={coin}
                        unit={'square_token'}
                        valueSize='lg'
                        unitSize='sm'
                        valueWeight='bold'
                      />
                    </Col>
                    <Row className='justify-center gap-xs'>
                      <Typography className='text-neutral-surface-60' size='xs'>
                        {t('clubPage_balance_value')}
                      </Typography>
                      <PaymentType
                        value={value}
                        size='xs'
                        color='text-neutral-surface-60'
                        numberSize='md'
                        numberColor='text-neutral-surface-80'
                        isBold={false}
                      />
                    </Row>
                  </Col>
                }
              >
                <div className='flex  flex-col max-h-[600px] overflow-y-auto'>
                  <InviteDetails
                    showTrades={false}
                    item={
                      {
                        ...portfolio.stats,
                        ...portfolio.referrals,
                        userLevel: portfolio.level?.current,
                        kyc: details.kyc,
                      } as UserReferralStats
                    }
                  />
                </div>
              </InformationDialogSimple>

              <Separator />
              <Row className='justify-center gap-2xs'>
                <Typography
                  color='text-neutral-surface-100'
                  weight='bold'
                  size='xs'
                >
                  {t('clubPage_balance_coefficient')}
                </Typography>
                <InformationDialog title={t('clubPage_balance_coefficient')}>
                  <div className='whitespace-pre-line'>
                    {tRich.rich('info_coefficient', {
                      link: (text) => (
                        <ButtonLink href='/agents' target='_blank'>
                          {text}
                        </ButtonLink>
                      ),
                    })}
                  </div>
                </InformationDialog>
              </Row>
              <MultiplyBadge value={coefficient} size='sm' />

              <Typography color='text-neutral-surface-60' size='xs'>
                {t('clubPage_balance_formula')}
              </Typography>

              <Col className='gap-y-xs'>
                <Row className='justify-between gap-md'>
                  <Col className='gap-md'>
                    <Typography className='text-neutral-surface-80' size='xs'>
                      {t('clubPage_balance_level_current')}
                    </Typography>
                    <LevelsBadge level={level} />
                  </Col>
                  <Col className='items-end gap-md'>
                    {level !== 5 ? (
                      <Row className='gap-3xs w-fit items-center'>
                        <ValueWithUnit
                          value={needToNextLevel}
                          // unit={'SQ2'}
                          valueSize='xs'
                          unitSize='2xs'
                          valueWeight='bold'
                        />
                        <Image
                          src='/images/club-gem.svg'
                          alt='coin'
                          width={20}
                          height={20}
                        />
                        <Typography
                          className='text-neutral-surface-80'
                          size='2xs'
                        >
                          {t('clubPage_balance_to_next')}
                        </Typography>
                      </Row>
                    ) : (
                      ''
                    )}

                    {level !== 5 ? <LevelsBadge level={level + 1} /> : ''}
                  </Col>
                </Row>

                <LevelsSlider
                  level={level}
                  value={[coin]}
                  max={coin + needToNextLevel}
                  min={0}
                  className='w-full my-xs'
                />
              </Col>
            </motion.div>
          ) : (
            <motion.div
              key='compact'
              initial={{ opacity: 0, height: 56 }}
              animate={{ opacity: 1, height: 56 }}
              exit={{ opacity: 0, height: 56 }}
              transition={{ duration: 0, ease: 'easeInOut' }}
              className='flex flex-col gap-md w-full justify-center items-center m-auto'
            >
              <Row
                className='w-full justify-between'
                onClick={() => setShowFull(true)}
              >
                <Row className='gap-xs'>
                  <Image
                    src='/images/club-gem.svg'
                    alt='coin'
                    width={30}
                    height={30}
                  />
                  <ValueWithUnit
                    value={coin}
                    unit={'square_token'}
                    valueSize='lg'
                    unitSize='2xs'
                    valueWeight='bold'
                  />
                </Row>
                <Row className='gap-xs w-fit'>
                  <Typography color='text-neutral-surface-100' size='xs'>
                    {t('clubPage_balance_coefficient_short')}
                  </Typography>
                  <MultiplyBadge value={coefficient} size='sm' />
                  {!isDesktop && (
                    <span
                      onClick={handleOpen}
                      className="bg-neutral-surface-dark p-xs hover:bg-neutral-surface-dark/50 rounded-full cursor-pointer focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground transition-opacity focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    >
                      <ChevronUpIcon />
                    </span>
                  )}
                </Row>
              </Row>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
      {!isDesktop && (
        <LinearBlur
          steps={8}
          // The blur radius of the blur in pixels at the peak of the gradient. Default is 64.
          strength={64}
          // How much of the blur is falloff. 0 means no falloff, 100 means the entire blur is falloff. Default is 100.
          falloffPercentage={100}
          // The tint applied to the blur. This can be any valid CSS color. Default is transparent.
          tint='rgba(0, 0, 0, 0.1)'
          // You can pass any div props to the component. Useful for positioning.
          style={{
            position: 'absolute',
            height: showFull ? '550px' : '120px',
            width: '100%',
            bottom: 0,
          }}
          side='bottom'
        />
      )}
    </Col>
  )
}
